import { datadogRum } from '@datadog/browser-rum';
import { DateTime } from '@ksyos/date-time';
import bind from 'bind-decorator';
import { kebabCase, omitBy, pickBy } from 'lodash';
import { useContext } from 'react';
import { useSelector } from 'react-redux';
import type { saveTypeEnum } from '@common/enums';
import { getServiceLabel } from '@common/customControls/getServiceLabel';
import { isDateInBetweenTwoDates } from '@common/utils/dates';
import { isPhotoAssetIdKey } from '@common/utils/photoAssetUtils';
import { CostInfoAndConsentModal } from '@frontend/components/elements/modals/costInfoAndConsentModal/CostInfoAndConsentModal';
import { getCostInfoAndConsent } from '@frontend/components/elements/modals/costInfoAndConsentModal/utilsCostInfoAndConsent';
import { translateKey } from '@frontend/translate';
import { scrollValidationErrorIntoCenterViewInScrollContainer } from '@frontend/utils';
import { asyncDebounce } from '@frontend/utils/asyncDebounce';
import { startView } from '@frontend/utils/hooks/useDatadogView';
import { prepareDiagnosisForSave } from '@frontend/utils/prepareDiagnosisForSave';
import type { IEpisodesAndJournals } from '../../../../common/validation/episodesAndJournal';
import type { IReduxState } from '../../../configureStore';
import type { ValidationErrors } from '../../../utils/validation';
import type { IAllergy } from '../../interfaces/allergy';
import type { IAsset } from '../../interfaces/asset';
import type { IDiagnosis } from '../../interfaces/diagnosis';
import type { IInsurer } from '../../interfaces/insurer';
import type { LabResult } from '../../interfaces/labresult';
import type { IMedication } from '../../interfaces/medication';
import type { IOrganizationGetOne } from '../../interfaces/organizationGetOne';
import type { IPractitioner } from '../../interfaces/practitioner';
import type { IProcess } from '../../interfaces/process';
import type { IRequestGetOne } from '../../interfaces/requestGetOne';
import type { IServiceGetOne } from '../../interfaces/serviceGetOne';
import type { IItemState, IPreviousRequestWithProcedure } from '../episodes/Episode';
import type { IEpisodeContext } from '../episodes/EpisodeContext';
import type { IPatientActiveInsuranceContext } from '../patients/PatientInsuranceHistoryContext';
import { CustomControlEntity } from '../../../../common/enums/customControlEntity';
import { EpisodeCategory } from '../../../../common/enums/episodeCategory';
import { RequestStatus } from '../../../../common/enums/requestStatus';
import { ServiceCode } from '../../../../common/enums/serviceCode';
import { ServiceType } from '../../../../common/enums/serviceType';
import { nullDiagnosis } from '../../../../common/validation/diagnosis';
import { getRequestValidationType } from '../../../../common/validation/getRequestValidationType';
import {
    getRequestValidationSchema,
    getSaveAndSaveAndSendValidationSchema,
} from '../../../../common/validation/request';
import { fetchJson } from '../../../store/fetch';
import { useEpisodeContext } from '../../../utils/hooks/useEpisodeContext';
import { isAutoSaveActiveByService } from '../../../utils/isAutoSaveActiveByService';
import { isServiceContractedByInsurer } from '../../../utils/isServiceContractedByInsurer';
import { patientUsesMedicationValidation } from '../../../utils/patientUsesMedicationValidation';
import {
    emptyValidationErrors,
    updateValidationErrorsForOne,
    validate,
    validationErrorsToString,
} from '../../../utils/validation';
import { Component } from '../../elements/Component';
import { InsurerCeilingModal } from '../../elements/InsurerCeilingModal';
import { initializeCustomControl } from '../customControls/initializeCustomControl';
import { loadCustomControl } from '../customControls/loadCustomControl';
import { getCareProviderIsActive } from '../customControls/utils/v1/getCareProviderIsActive';
import { validateCustomControlData } from '../customControls/validateCustomControlData';
import { validateDiagnosis } from '../customControls/validateDiagnosis';
import { validateJournals } from '../customControls/validateJournals';
import { validateMedications } from '../customControls/validateMedications';
import { validateOneCustomControlData } from '../customControls/validateOneCustomControlData';
import { EpisodeContext } from '../episodes/EpisodeContext';
import { ReloadEpisode } from '../episodes/reloadEpisode';
import { PatientActiveInsuranceContext } from '../patients/PatientInsuranceHistoryContext';

interface IRequestItemAddEditProps {
    disableFormAutosave: boolean;
    formId: string;
    isFirstItem: boolean;
    isLastItem: boolean;
    itemState?: IItemState;
    newProcessCustomIdentifier?: string;
    onCancel: (requestId: string | undefined) => Promise<void>;
    onLoaded?: () => void;
    onReloadPatient?: () => Promise<void>;
    onSave: (requestId: string) => Promise<void>;
    previousRequest?: IPreviousRequestWithProcedure;
    process?: IProcess;
    reloadProcess: (mode?: ReloadEpisode) => Promise<void>;
    removeProcess?: () => Promise<void>;
    request?: IRequestGetOne;
    scrollToNewProcess?: () => void;
    serviceId: string;
    updateNewRequestPreviousRequest?: (requestId: string) => Promise<void>;
}

interface IRequestItemAddEditReduxProps {
    employmentId: string;
    needsSupervisor: boolean;
    organizationId: string;
}

export interface IRequestAddEdit<T = any> {
    allergies?: IAllergy[];
    assets: IAsset[];
    customControlData: T;
    diagnosis: IDiagnosis;
    draftCareProviderOrganizationId: string | null;
    draftCareProviderPractitionerId: string | null;
    hasCostConsentPatient: boolean | null;
    id?: string;
    patientId: string;
    patientUsesMedication: boolean | null;
    previousRequestId: string | null;
    question: string | null;
    requestEpisodesAndJournal?: IEpisodesAndJournals;
    requestLabResults?: LabResult[];
    requestMedications?: IMedication[];
    selectedDiagnosticReportConsumerPractitionerId?: string | null;
    selectedDiagnosticReportProviderOrganizationId: string | null;
    selectedDiagnosticReportProviderPractitionerId: string | null;
    selectedProcedureProviderOrganizationId: string | null;
    selectedProcedureProviderPractitionerId: string | null;
    serviceId: string;
    supervisorPractitionerId?: string;
}

interface IRequestItemAddEditState {
    canAutoSave: boolean;
    errors: ValidationErrors;
    insurer?: IInsurer;
    insurerBlockedIsModalShown: boolean;
    isFirstTimeSaving: boolean;
    organization?: IOrganizationGetOne;
    price?: number;
    request?: IRequestAddEdit;
    service?: IServiceGetOne;
    showCostInfoAndConsentModal: boolean;
    supervisor?: IPractitioner;
    vatRate?: number;
}

export function RequestItemAddEdit(props: IRequestItemAddEditProps) {
    const episodeContext = useEpisodeContext();
    const patientInsuranceHistoryContext = useContext(PatientActiveInsuranceContext);
    const employmentId = useSelector((state: IReduxState) => state.authentication.selectedEmploymentId);
    const organizationId = useSelector((state: IReduxState) => state.authentication.organizationId);
    const needsSupervisor = useSelector((state: IReduxState) => state.authentication.needsSupervisor);

    return (
        <RequestItemAddEditWrapper
            {...props}
            {...episodeContext}
            {...patientInsuranceHistoryContext}
            employmentId={employmentId}
            needsSupervisor={needsSupervisor}
            organizationId={organizationId}
        />
    );
}

class RequestItemAddEditWrapper extends Component<
    IRequestItemAddEditProps & IRequestItemAddEditReduxProps & IEpisodeContext & IPatientActiveInsuranceContext,
    IRequestItemAddEditState
> {
    constructor(
        props: IRequestItemAddEditProps &
            IRequestItemAddEditReduxProps &
            IEpisodeContext &
            IPatientActiveInsuranceContext,
        context?: any
    ) {
        super(props, context);

        if (this.props.request && (!this.props.request.patientId || !this.props.request.serviceId)) {
            throw Error('Missing patientId or requestId for addEdit');
        }

        this.state = {
            errors: emptyValidationErrors,
            insurerBlockedIsModalShown: false,
            showCostInfoAndConsentModal: false,
            canAutoSave: false,
            isFirstTimeSaving: !this.props.request?.id,
        };
    }

    private debouncedSave = asyncDebounce(this.saveRequest.bind(this), 300);

    public async componentDidMount() {
        await this.load(this.props.serviceId);
        this.scrollToNewProcess();

        const costAndConsentInfo = await getCostInfoAndConsent(this.props.serviceId, this.props.request?.id);

        await this.setState({
            ...costAndConsentInfo,
        });

        if (this.props.onLoaded) {
            this.props.onLoaded();
        }

        if (this.state.service?.episodeCategory) {
            startView(
                `/episode/${this.state.service.episodeCategory.name}/${this.state.service.code.toLowerCase()}_Request`
            );
        }

        await this.setState({ canAutoSave: true });
    }

    public async componentDidUpdate(
        prevProps: Readonly<IRequestItemAddEditProps & IRequestItemAddEditReduxProps & IEpisodeContext>,
        prevState: Readonly<IRequestItemAddEditState>
    ) {
        if (prevProps.serviceId !== this.props.serviceId) {
            await this.setState({ service: undefined, request: undefined });
            await this.load(this.props.serviceId);

            const costAndConsentInfo = await getCostInfoAndConsent(this.props.serviceId, this.props.request?.id);

            await this.setState({
                ...costAndConsentInfo,
            });
        }

        if (
            prevState.isFirstTimeSaving !== this.state.isFirstTimeSaving ||
            prevState.request?.id !== this.state.request?.id
        ) {
            await this.setState({ canAutoSave: this.state.isFirstTimeSaving || !!this.state.request?.id });
        }
    }

    public componentWillUnmount() {
        this.props.setAutoSaveStatus('idle');
    }

    public render() {
        if (!this.state.service || !this.state.request) {
            return null;
        }

        if (this.state.insurer && this.state.insurerBlockedIsModalShown) {
            return (
                <InsurerCeilingModal
                    insurer={this.state.insurer}
                    isOpen={this.state.insurerBlockedIsModalShown}
                    onClose={async () => {
                        await this.setState({ insurerBlockedIsModalShown: false });
                        this.props.onCancel(this.state.request ? this.state.request.id : undefined);
                    }}
                ></InsurerCeilingModal>
            );
        }

        if (this.state.showCostInfoAndConsentModal) {
            return (
                <CostInfoAndConsentModal
                    isOpen={this.state.showCostInfoAndConsentModal}
                    onCancel={() => {
                        this.setState({ showCostInfoAndConsentModal: false });
                    }}
                    onSubmit={(consent) => {
                        this.handleCostInfoAndConsentModalSubmit(consent);
                    }}
                    patientId={this.state.request.patientId}
                    price={this.state.price || 0}
                    serviceName={
                        getServiceLabel(this.state.service.code, 'costInfoAndConsentModal') ||
                        translateKey('common.service')
                    }
                    vatRate={this.state.vatRate || 0}
                />
            );
        }

        // CS-3667 for version not nullable
        const version = this.props.request
            ? this.props.request.customControlVersion
            : this.state.service.customControlVersion;
        if (!version) {
            throw new Error('Version or employmentId or organizationId not set');
        }
        const CustomControl = loadCustomControl(
            version,
            CustomControlEntity.RequestAddEdit,
            this.state.service.code,
            this.state.service.serviceType
        );

        return (
            <EpisodeContext.Consumer>
                {(context) =>
                    this.state.request &&
                    this.state.service && (
                        <CustomControl
                            employmentId={this.props.employmentId}
                            errors={this.state.errors}
                            formId={this.props.formId}
                            handleChange={async (name, value) => await this.handleChange(name, value)}
                            handleChangeCustomControl={async (values) => await this.handleChangeCustomControl(values)}
                            isAutoSaveActive={isAutoSaveActiveByService(
                                this.state.service!.code,
                                this.props.disableFormAutosave,
                                this.getVersion(),
                                this.state.request!.customControlData
                            )}
                            isFirstItem={this.props.isFirstItem}
                            isHighPrivacy={(this.state.organization && this.state.organization.isHighPrivacy) || false}
                            itemState={this.props.itemState}
                            newProcessCustomIdentifier={this.props.newProcessCustomIdentifier}
                            onCancel={() => this.props.onCancel(this.state.request ? this.state.request.id : undefined)}
                            onSave={this.props.onSave}
                            onSaveRequest={async (saveType) => {
                                context?.changeShowRecommendations(true);
                                await this.setState({ canAutoSave: false });

                                return await this.debouncedSave(saveType);
                            }}
                            organizationId={this.props.organizationId}
                            previousRequest={this.props.previousRequest}
                            process={this.props.process}
                            reloadAssets={async () => await this.reloadAssets()}
                            reloadPatient={this.props.onReloadPatient}
                            reloadProcess={this.props.reloadProcess}
                            removeProcess={this.props.removeProcess}
                            request={this.state.request}
                            revertRequestChanges={this.revertRequestChanges.bind(this)}
                            scrollToNewProcess={() => this.scrollToNewProcess()}
                            service={this.state.service}
                            updateNewRequestPreviousRequest={this.handleSelectingPreviousRequest}
                            validateOne={async (name) => await this.validateOne(name)}
                            validateOneCustomControl={async (name) => await this.validateOneCustomControl(name)}
                            validateRequest={async (saveType) => await this.validateRequest(saveType)}
                        />
                    )
                }
            </EpisodeContext.Consumer>
        );
    }

    @bind
    private async handleSelectingPreviousRequest(previousRequestId: string) {
        if (!this.props.updateNewRequestPreviousRequest) {
            return;
        }
        await this.handleChange<IRequestAddEdit, 'previousRequestId'>('previousRequestId', previousRequestId);
        const validatedRequest = await this.validateRequest('draft');
        if (validatedRequest) {
            const requestId = this.state.request?.id ?? (await this.debouncedSave('draft'));
            await this.props.reloadProcess(ReloadEpisode.reloadAll);
            await this.props.updateNewRequestPreviousRequest(requestId);
        }
    }

    private async handleChange<T, K extends keyof T>(name: K, value: T[K]) {
        await this.setState(({ request }) => ({ request: { ...request, [name]: value } }));
        if (name === 'assets') {
            await this.autoSaveRequest(this.state.errors);
        }
    }

    private async handleCostInfoAndConsentModalSubmit(costConsentPatient: boolean) {
        await this.handleChange<IRequestAddEdit, 'hasCostConsentPatient'>('hasCostConsentPatient', costConsentPatient);
        await this.setState({ showCostInfoAndConsentModal: false });
        await this.props.onReloadPatient?.();
    }

    private async validateOne(name: string) {
        if (!this.state.service) {
            return;
        }

        if (name === 'selectedDiagnosticReportProviderOrganizationId') {
            await this.setState((state) =>
                state.request
                    ? {
                          errors: this.addSelectedDiagnosticReportProviderValidation(
                              state.request.selectedDiagnosticReportProviderOrganizationId,
                              state.errors
                          ),
                      }
                    : undefined
            );
        }

        if (name === 'draftCareProviderOrganizationId') {
            await this.setState((state) =>
                state.request
                    ? {
                          errors: this.addCareProviderValidation(
                              state.request.draftCareProviderOrganizationId,
                              state.errors
                          ),
                      }
                    : undefined
            );
        }

        if (name === 'diagnosis') {
            await this.setState((state) =>
                state.request
                    ? {
                          errors: validateDiagnosis(
                              this.getVersion(),
                              CustomControlEntity.RequestAddEdit,
                              state.service!.code,
                              state.request.diagnosis,
                              state.errors,
                              state.request.customControlData
                          ),
                      }
                    : undefined
            );
        }

        if (name === 'requestMedications') {
            await this.setState((state) =>
                state.request
                    ? { errors: validateMedications(state.errors, state.request.requestMedications) }
                    : undefined
            );
        }

        if (name === 'requestEpisodesAndJournal') {
            await this.setState((state) =>
                state.request
                    ? {
                          errors: validateJournals(
                              state.errors,
                              state.request.requestEpisodesAndJournal?.journalEntries
                          ),
                      }
                    : undefined
            );
        }

        const validationType = this.getRequestValidationType('final');
        const validationErrors = updateValidationErrorsForOne(
            getRequestValidationSchema(validationType),
            this.state.request,
            this.state.errors,
            name
        );

        if (!validationErrors.isEmpty()) {
            this.props.setAutoSaveStatus('idle');
        }

        await this.setState({ errors: validationErrors });

        await this.autoSaveRequest(validationErrors);
    }

    private async handleChangeCustomControl(values: any) {
        await this.setState(({ request }) => ({
            request: { ...request, customControlData: { ...request!.customControlData, ...values } },
        }));

        if (('files' in values && Object.keys(values).length === 1) || Object.keys(values).some(isPhotoAssetIdKey)) {
            await this.autoSaveRequest(this.state.errors);
        }
    }

    private async validateOneCustomControl(name: string) {
        if (!this.state.service) {
            return;
        }

        const customControlName = 'customControlData.' + name;
        const validationType = this.getRequestValidationType('final');

        let errors = updateValidationErrorsForOne(
            getRequestValidationSchema(validationType),
            this.state.request,
            this.state.errors,
            customControlName
        );
        errors = validateOneCustomControlData(
            this.getVersion(),
            CustomControlEntity.RequestAddEdit,
            this.state.service!.code,
            this.state.request!.customControlData,
            name,
            errors,
            this.props.process
        );

        await this.setState({ errors });
        await this.autoSaveRequest(errors);
    }

    private scrollToNewProcess() {
        if (this.props.scrollToNewProcess && document.getElementsByName('newProcess').length === 1) {
            this.props.scrollToNewProcess();
        }
    }

    private async load(serviceId: string) {
        await this.loadService(serviceId);

        if (!this.state.service) {
            throw new Error('Failed to load service for new request');
        }

        const careProviderIsActive = await getCareProviderIsActive(
            this.props.patient.careProviderPractitionerId,
            this.props.patient.careProviderOrganizationId
        );

        if (this.props.request) {
            const request = {
                id: this.props.request.id,
                previousRequestId: this.props.request.previousRequestId,
                patientId: this.props.patient.id,
                serviceId: this.props.request.serviceId,
                selectedProcedureProviderOrganizationId: this.props.request.selectedProcedureProviderOrganizationId,
                selectedProcedureProviderPractitionerId: this.props.request.selectedProcedureProviderPractitionerId,
                selectedDiagnosticReportProviderOrganizationId:
                    this.props.request.selectedDiagnosticReportProviderOrganizationId,
                selectedDiagnosticReportProviderPractitionerId:
                    this.props.request.selectedDiagnosticReportProviderPractitionerId,
                selectedDiagnosticReportConsumerPractitionerId: null,
                customControlData: this.props.request.customControlData,
                diagnosis: this.props.request.diagnosis || { ...nullDiagnosis },
                question: this.props.request.question,
                assets: this.props.request.assets ? this.props.request.assets : [],
                draftCareProviderOrganizationId:
                    this.props.request.draftCareProviderOrganizationId ||
                    (this.props.patient.careProviderOrganizationId && careProviderIsActive
                        ? this.props.patient.careProviderOrganizationId
                        : null),
                draftCareProviderPractitionerId:
                    this.props.request.draftCareProviderPractitionerId ||
                    (this.props.patient.careProviderPractitionerId && careProviderIsActive
                        ? this.props.patient.careProviderPractitionerId
                        : null),
                supervisorPractitionerId: this.props.request.supervisorPractitionerId,
                patientUsesMedication: this.props.request.patientUsesMedication,
                hasCostConsentPatient: this.props.request.hasCostConsentPatient,
            };
            await this.setState({ request });
        } else {
            const customControlData = this.state.service.customControlVersion
                ? initializeCustomControl(
                      this.state.service.customControlVersion,
                      CustomControlEntity.RequestAddEdit,
                      this.state.service.code,
                      this.state.service.serviceType
                  )
                : null;
            await this.setState({
                request: {
                    previousRequestId: this.props.previousRequest ? this.props.previousRequest.id : null,
                    patientId: this.props.patient.id,
                    serviceId: serviceId,
                    selectedProcedureProviderOrganizationId: null,
                    selectedProcedureProviderPractitionerId: null,
                    selectedDiagnosticReportProviderPractitionerId: null,
                    selectedDiagnosticReportProviderOrganizationId: null,
                    selectedDiagnosticReportConsumerPractitionerId: null,
                    customControlData: customControlData,
                    diagnosis: { ...nullDiagnosis },
                    question: null,
                    assets: [],
                    draftCareProviderOrganizationId:
                        this.props.patient.careProviderOrganizationId && careProviderIsActive
                            ? this.props.patient.careProviderOrganizationId
                            : null,
                    draftCareProviderPractitionerId:
                        this.props.patient.careProviderPractitionerId && careProviderIsActive
                            ? this.props.patient.careProviderPractitionerId
                            : null,
                    patientUsesMedication: null,
                    hasCostConsentPatient: null,
                },
            });
        }

        await this.loadOrganization();

        if (!this.props.request || this.props.request.status === RequestStatus.draft) {
            await this.checkInsurerCeiling();
        }
    }

    private async loadService(serviceId: string) {
        const url = `/api/services/${serviceId}`;
        const result = await fetchJson(url, 'GET');
        await this.setState({ service: result.json });
    }

    private async loadOrganization() {
        const url = `/api/organizations/${this.props.organizationId}`;
        const result = await fetchJson(url, 'GET');
        await this.setState({ organization: result.json });
    }

    private getVersion() {
        return this.props.request
            ? this.props.request.customControlVersion
            : (this.state.service!.customControlVersion ?? 1);
    }

    private addSelectedDiagnosticReportProviderValidation(
        selectedDiagnosticReportProviderOrganizationId: string | null,
        errors: ValidationErrors
    ) {
        if (
            this.state.service &&
            !this.state.service.userIsGrader &&
            this.state.service.userIsConsumerGraderSelector &&
            this.state.service.serviceType !== ServiceType.examination &&
            !selectedDiagnosticReportProviderOrganizationId
        ) {
            return errors.set('selectedDiagnosticReportProviderOrganizationId', 'graderRequired');
        }

        return errors;
    }

    private addCareProviderValidation(draftCareProviderOrganizationId: string | null, errors: ValidationErrors) {
        if (
            this.state.organization &&
            !this.state.organization.isHighPrivacy &&
            !draftCareProviderOrganizationId &&
            !this.state.service?.noGeneralPractitionerInvolved
        ) {
            return errors.set('draftCareProviderOrganizationId', 'any_required');
        }

        return errors;
    }

    private addServiceConsumerSupervisorValidation(
        supervisorPractitionerId: string | undefined,
        serviceType: ServiceType,
        errors: ValidationErrors
    ) {
        if (serviceType === ServiceType.consultation && !supervisorPractitionerId && this.props.needsSupervisor) {
            return errors.set('supervisorPractitionerId', 'any_required');
        }

        return errors;
    }

    private addQuestionValidation(
        question: string | null,
        serviceType: ServiceType,
        episodeCategory: string | undefined,
        code: ServiceCode,
        errors: ValidationErrors
    ) {
        if (
            ((serviceType === ServiceType.consultation &&
                [EpisodeCategory.pulmonology, EpisodeCategory.cardiology].includes(
                    episodeCategory as EpisodeCategory
                ) &&
                code !== ServiceCode.PILOT) ||
                code === ServiceCode.OrthC) &&
            !question
        ) {
            return errors.set('question', 'any_required');
        }

        return errors;
    }

    private addPatientUsesMedicationValidation(
        patientUsesMedication: boolean | null,
        serviceCode: ServiceCode,
        serviceType: ServiceType,
        errors: ValidationErrors
    ) {
        return patientUsesMedicationValidation('request', serviceCode, serviceType, patientUsesMedication, errors);
    }

    private async costConsentPatientValidation(hasCostConsentPatient: boolean | null) {
        if (!hasCostConsentPatient) {
            const { showCostInfoAndConsentModal } = await getCostInfoAndConsent(
                this.props.serviceId,
                this.props.request?.id
            );

            return !showCostInfoAndConsentModal;
        }

        return true;
    }

    private getRequestValidationType(draftOrFinal: 'draft' | 'final') {
        if (!this.state.service) {
            return;
        }

        const isEdit = (this.props.request && this.props.request.id) || (this.state.request && this.state.request.id);

        return getRequestValidationType(
            this.state.service.code,
            this.state.service.serviceType,
            this.getVersion(),
            isEdit ? 'edit' : 'add',
            draftOrFinal
        );
    }

    private async validateRequest(saveType: 'final' | 'draft'): Promise<boolean> {
        if (!this.state.service || !this.state.request) {
            return false;
        }

        const validationType = this.getRequestValidationType(saveType);
        const validationResult = validate(getRequestValidationSchema(validationType), this.state.request);

        let errors = validationResult.errors;
        if (saveType === 'final') {
            const validConsent = await this.costConsentPatientValidation(this.state.request.hasCostConsentPatient);
            if (!validConsent) {
                await this.setState({ showCostInfoAndConsentModal: true });

                return false;
            }
            errors = validateCustomControlData(
                this.getVersion(),
                CustomControlEntity.RequestAddEdit,
                this.state.service.code,
                this.state.request.customControlData,
                errors,
                this.props.process
            );
            errors = this.addSelectedDiagnosticReportProviderValidation(
                this.state.request.selectedDiagnosticReportProviderOrganizationId,
                errors
            );
            errors = this.addCareProviderValidation(this.state.request.draftCareProviderOrganizationId, errors);
            errors = this.addServiceConsumerSupervisorValidation(
                this.state.request.supervisorPractitionerId,
                this.state.service.serviceType,
                errors
            );
            errors = this.addQuestionValidation(
                this.state.request.question,
                this.state.service.serviceType,
                this.state.service.episodeCategory?.name,
                this.state.service.code,
                errors
            );
            errors = this.addPatientUsesMedicationValidation(
                this.state.request.patientUsesMedication,
                this.state.service.code,
                this.state.service.serviceType,
                errors
            );
            errors = validateDiagnosis(
                this.getVersion(),
                CustomControlEntity.RequestAddEdit,
                this.state.service.code,
                this.state.request.diagnosis,
                errors,
                this.state.request.customControlData
            );
        }

        errors = validateMedications(errors, this.state.request.requestMedications);
        errors = validateJournals(errors, this.state.request.requestEpisodesAndJournal?.journalEntries);

        await this.setState({ errors });
        if (errors.size > 0) {
            scrollValidationErrorIntoCenterViewInScrollContainer(this.props.formId, errors);

            return false;
        }

        return true;
    }

    private async saveRequest(saveType: 'draft' | 'final') {
        this.props.setAutoSaveStatus('saving');

        if (!this.state.request) {
            return;
        }

        await this.setState({ isFirstTimeSaving: false });

        let url = '/api/requests';
        url += saveType === 'final' ? '/saveAndSend' : '/save';

        const validationType = this.getRequestValidationType(saveType);
        const validatedRequest = validate(getSaveAndSaveAndSendValidationSchema(validationType), {
            assets: this.state.request.assets,
            request: this.state.request,
            medications: this.state.request.requestMedications,
            labResults: this.state.request.requestLabResults,
            allergies: this.state.request.allergies,
            diagnosis: prepareDiagnosisForSave(this.state.request.diagnosis, saveType as saveTypeEnum),
            episodesAndJournals: this.state.request.requestEpisodesAndJournal,
            validationType,
        });

        if (validatedRequest.errors.size > 0) {
            if (
                isAutoSaveActiveByService(this.state.service!.code, this.props.disableFormAutosave) &&
                saveType === 'draft'
            ) {
                return;
            } else {
                throw new Error(
                    `Errors in save request validation:\n${validationErrorsToString(validatedRequest.errors)}`
                );
            }
        }
        const result = await fetchJson(url, 'POST', validatedRequest.item);

        if (saveType === 'final') {
            datadogRum.addAction(`Save ${kebabCase(this.state.service?.code.toLowerCase() + '_Request')} complete`);
        }

        const requestResult = (await fetchJson(`/api/requests/${result.json.id}?showAssets=true`, 'GET')).json;

        await this.setState(({ request }) => ({
            request: {
                ...request,
                customControlData: { ...request?.customControlData, files: requestResult?.customControlData?.files },
                id: requestResult.id,
                assets: requestResult.assets,
            },
        }));

        if (this.props.onReloadPatient && saveType === 'final') {
            await this.props.onReloadPatient();
        }

        this.props.setAutoSaveStatus('saved');

        return result.json.id;
    }

    private async reloadAssets() {
        if (this.state.request?.id) {
            const requestResult = (await fetchJson(`/api/requests/${this.state.request.id}?showAssets=true`, 'GET'))
                .json;

            await this.setState(({ request }) => ({
                request: {
                    ...request,
                    assets: requestResult.assets,
                    customControlData: {
                        ...omitBy(request?.customControlData, (_value, key) => isPhotoAssetIdKey(key)),
                        ...pickBy(requestResult.customControlData, (_value, key) => isPhotoAssetIdKey(key)),
                    },
                },
            }));
        }
    }

    private async checkInsurerCeiling() {
        const activeInsuranceHistory = this.props.activeInsurance;
        if (activeInsuranceHistory?.insurer) {
            const insurerIsBlocked = isDateInBetweenTwoDates(
                DateTime.now().format('YYYY-MM-DD'),
                activeInsuranceHistory.insurer.turnoverCeilingStartDate,
                activeInsuranceHistory.insurer.turnoverCeilingEndDate
            );
            if (insurerIsBlocked) {
                const isServiceContracted = await isServiceContractedByInsurer(
                    activeInsuranceHistory.insurer.id,
                    this.props.serviceId
                );
                if (isServiceContracted) {
                    await this.setState({ insurer: activeInsuranceHistory.insurer, insurerBlockedIsModalShown: true });
                }
            }
        }
    }

    private async autoSaveRequest(errors: ValidationErrors) {
        if (
            errors.size === 0 &&
            isAutoSaveActiveByService(
                this.state.service!.code,
                this.props.disableFormAutosave,
                this.getVersion(),
                this.state.request!.customControlData
            ) &&
            this.state.canAutoSave
        ) {
            await this.debouncedSave('draft');
        }
    }

    private async revertRequestChanges() {
        if (this.props.request) {
            const originalRequest: Partial<IRequestAddEdit> | undefined = {
                ...this.state.request,
                ...this.props.request,
            };

            await this.setState({ request: originalRequest as IRequestAddEdit });
            await this.debouncedSave('draft');
        }
    }
}
